import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  CreateEgressPolicyRequest,
  DeleteEgressPolicyResponse,
  EffectiveEgressPolicyResponse,
  GetOrgEgressPolicyResponse,
  ListEgressPoliciesResponse,
  MutateEgressPolicyResponse,
  PutOrgEgressPolicyRequest,
  PutOrgEgressPolicyResponse,
  UpdateEgressPolicyRequest,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  createEgressPolicy,
  deleteEgressPolicy,
  getEffectiveEgressPolicy,
  getOrgEgressPolicy,
  listEgressPolicies,
  putOrgEgressPolicy,
  updateEgressPolicy,
} from './egressPolicies.ts';

export const EGRESS_POLICIES_QUERY_KEY = ['egress-policies'] as const;

export function orgEgressPolicyKey(orgId: string): readonly ['egress-policies', 'orgs', string] {
  return [...EGRESS_POLICIES_QUERY_KEY, 'orgs', orgId] as const;
}

export function useEgressPolicies(enabled = true): UseQueryResult<ListEgressPoliciesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: EGRESS_POLICIES_QUERY_KEY,
    queryFn: ({ signal }) => listEgressPolicies(fetchImpl, signal),
    enabled,
  });
}

export function useOrgEgressPolicy(orgId: string): UseQueryResult<GetOrgEgressPolicyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgEgressPolicyKey(orgId),
    queryFn: ({ signal }) => getOrgEgressPolicy(orgId, fetchImpl, signal),
    enabled: orgId.length > 0,
  });
}

export function useCreateEgressPolicy(): UseMutationResult<
  MutateEgressPolicyResponse,
  Error,
  CreateEgressPolicyRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createEgressPolicy(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_POLICIES_QUERY_KEY });
    },
  });
}

export function useUpdateEgressPolicy(): UseMutationResult<
  MutateEgressPolicyResponse,
  Error,
  { readonly policyId: string; readonly body: UpdateEgressPolicyRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, body }) => updateEgressPolicy(policyId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_POLICIES_QUERY_KEY });
    },
  });
}

export function useDeleteEgressPolicy(): UseMutationResult<DeleteEgressPolicyResponse, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policyId) => deleteEgressPolicy(policyId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_POLICIES_QUERY_KEY });
    },
  });
}

export function usePutOrgEgressPolicy(): UseMutationResult<
  PutOrgEgressPolicyResponse,
  Error,
  { readonly orgId: string; readonly body: PutOrgEgressPolicyRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => putOrgEgressPolicy(orgId, body, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: EGRESS_POLICIES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: orgEgressPolicyKey(vars.orgId) });
    },
  });
}

export function useEffectiveEgressPolicy(
  query: { readonly orgId?: string; readonly ingress: string; readonly upstreamId: string },
  enabled: boolean,
): UseQueryResult<EffectiveEgressPolicyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...EGRESS_POLICIES_QUERY_KEY, 'effective', query],
    queryFn: ({ signal }) => getEffectiveEgressPolicy(query, fetchImpl, signal),
    enabled,
  });
}
